import React, {useEffect, useState} from 'react';

import {createAnalysis} from 'analysis-tool/AnalysisActions';
import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import {
  isDomainConfigVariable,
  prepareFileVariableValue,
} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import SimpleAnalysisTemplateSelector from 'analysis-tool/templates/SimpleAnalysisTemplateSelector';
import AnalysisTemplateVariablesForm from 'analysis-tool/templates/variables/AnalysisTemplateVariablesForm';
import {ObjectType} from 'app-core/framework/CoreObjectConstants';
import {assert} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {isValueValid} from 'pipelines/StepShapeUtils';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';
import {JSONValue} from 'util/Types';

import CreateAnalysisForm from './CreateAnalysisForm';
import useCreateAnalysisState from './useCreateAnalysisState';

/**
 * Check if a variable should be shown in <AnalysisTemplateVariablesForm>.
 *
 * Folder variables are hidden, because a folder selector is separately shown for the Analysis as a whole.
 *
 * `sectionIndex` is hidden, because it could be confusing to expose directly to users.
 */
function shouldShowVariableInForm(
  variable: AnalysisTemplateSelectorTemplate['configVariables'][number]
): boolean {
  return !isDomainConfigVariable(variable, 'FOLDER') && variable.configVariableKey !== 'sectionIndex';
}

export interface RunAnalysisTemplateModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  /**
   * Mapping from domain object types to default values.
   *
   * If `variableDefaults` is provided, only templates with matching variable types will be selectable.
   *
   * For example, `{FILE: file_123}` will limit the listing of analysis templates to only those with a FILE
   * variable. Templates with additional variables, in addition to the FILE variable, will also be shown.
   *
   * This does not yet account for 'array'-type variables.
   */
  variableDefaults?: Partial<Record<ObjectType, string>>;
  initialAnalysisName?: string;
}

/**
 * Modal to run a variable-based analysis template, by entering values for its variables.
 *
 * Analysis templates with data frame variables (i.e. variable input tables) are not supported.
 *
 * If `variableDefaults` is passed, only templates with matching variable types will be selectable. For
 * example, if `variableDefaults` is `{FILE: file_123}`, only analysis templates with a FILE variable will be
 * shown. Any FILE variable will default to `file_123`.
 */
export default function RunAnalysisTemplateModal({
  onRequestSubmit,
  onRequestClose,
  variableDefaults,
  initialAnalysisName,
}: RunAnalysisTemplateModalProps): JSX.Element {
  const {createAnalysisState, setCreateAnalysisState, requestParamsInfo} = useCreateAnalysisState({
    initialAnalysisName,
  });
  const [template, setTemplate] = useState<AnalysisTemplateSelectorTemplate | null>(null);
  const [variableBindings, setVariableBindings] = useState<Record<string, JSONValue>>({});

  const variablesToShowInForm =
    template?.configVariables.filter((variable) => shouldShowVariableInForm(variable)) ?? [];

  const variableBindingsAreComplete = variablesToShowInForm.every((variable) =>
    isValueValid(variableBindings[variable.id] ?? null, variable.variableTypeConfigSpec, {})
  );

  // Clear variable bindings when the user selects a new template.
  useEffect(() => {
    setVariableBindings(
      // Apply default variable values
      Object.fromEntries(
        Object.entries(variableDefaults ?? {}).flatMap(
          ([objectType, defaultValue]: [ObjectType, string]) =>
            template?.configVariables
              .filter((variable) => isDomainConfigVariable(variable, objectType))
              .map((variable) => [variable.id, prepareFileVariableValue(variable, defaultValue)]) ?? []
        )
      )
    );
  }, [template, variableDefaults]);

  async function handleRequestSubmit(): Promise<void> {
    assert(requestParamsInfo.isComplete, 'Expected create analysis request params to be complete');
    assert(template != null, 'Expected template to be selected');
    FolderDefaults.set(FolderDefaultsKey.ANALYSIS, requestParamsInfo.requestParams.folderId);

    const variableBindingsArray = template.configVariables.map((variable) => ({
      variableId: variable.id,
      variableValue: isDomainConfigVariable(variable, 'FOLDER')
        ? requestParamsInfo.requestParams.folderId
        : (variableBindings[variable.id] ?? null),
    }));

    const params = {
      ...requestParamsInfo.requestParams,
      templateRun: {
        templateId: template.id,
        variableBindings: variableBindingsArray,
      },
    };
    await withLoadingScreen(createAnalysis(params), 'Creating analysis...');

    onRequestSubmit();
  }

  return (
    <Modal
      title="Run analysis template"
      onRequestSubmit={handleRequestSubmit}
      onRequestClose={onRequestClose}
      buttons={[
        {
          submit: true,
          text: 'Create',
          style: ButtonStyle.SECONDARY,
          disabled: template == null || !requestParamsInfo.isComplete || !variableBindingsAreComplete,
        },
      ]}
    >
      <div className="u-spacing-bottom">
        <FieldLabel label="Analysis Template" isRequired={true} />
        <SimpleAnalysisTemplateSelector
          template={template}
          onSelectTemplate={setTemplate}
          filterTemplate={(template) =>
            // Data frame variables (i.e. variable input tables) are not supported from this workflow
            template.dataFrameVariables.length === 0 &&
            // If `variableDefaults` are provided, only list templates with matching variables
            (variableDefaults == null ||
              Object.keys(variableDefaults).every((objectType: ObjectType) =>
                template.configVariables.some((variable) => isDomainConfigVariable(variable, objectType))
              ))
          }
        />
      </div>
      <CreateAnalysisForm
        createAnalysisState={createAnalysisState}
        setCreateAnalysisState={setCreateAnalysisState}
        hideDataSharingWarningBanner={false}
      />
      {template && (
        <div className="u-small-spacing-top">
          <AnalysisTemplateVariablesForm
            clientStateId={`run-template-${template.id}`}
            variables={variablesToShowInForm}
            variableBindings={variableBindings}
            onVariableBindingsChange={setVariableBindings}
          />
        </div>
      )}
    </Modal>
  );
}
